import type React from 'react';
import {Box, Stack, Tooltip, Typography} from '@mui/material';
import {TIER_COLOR} from '../../domain/shouldCost';
import type {PriceModel} from '../../types/shouldCostTypes';
import {ProfileSection} from '../components/ProfileSection';
import {SHOULD_COST_HELP} from '../../domain/shouldCostHelp';

const SOURCES = {live: 'exchange and official indices', modeled: 'proxies such as consumer prices for labour',
    manual: 'customer uploads', estimated: 'fixed lines without a driver'} as const;

/** Accuracy score (spec 4.9): the share of cost per data tier, weighted live 1.0, modeled 0.8, manual 0.6, estimated 0.4. */
export const AccuracyCard: React.FC<{model: PriceModel}> = ({model}) => (
    <ProfileSection title="Accuracy score" help={SHOULD_COST_HELP.accuracy}>
        <Stack direction={{xs: 'column', sm: 'row'}} gap={3} alignItems={{sm: 'center'}}>
            <Box sx={{textAlign: 'center', minWidth: 80}}>
                <Typography variant="h4" sx={{fontWeight: 600, lineHeight: 1}}>{model.accuracy.score ?? '—'}</Typography>
                <Typography variant="caption" color="text.secondary">of 100</Typography>
            </Box>
            <Stack gap={1} sx={{flex: 1, minWidth: 0}}>
                <Stack direction="row" sx={{height: 10, borderRadius: 1, overflow: 'hidden'}}>
                    {model.accuracy.byTier.map(t => (
                        <Tooltip key={t.tier} title={`${t.label}: ${t.sharePct}% of cost`}>
                            <Box sx={{width: `${t.sharePct}%`, bgcolor: TIER_COLOR[t.tier]}}/>
                        </Tooltip>
                    ))}
                </Stack>
                <Stack direction="row" gap={2} flexWrap="wrap">
                    {model.accuracy.byTier.map(t => (
                        <Stack key={t.tier} direction="row" gap={0.75} alignItems="center">
                            <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: TIER_COLOR[t.tier]}}/>
                            <Typography variant="caption">{`${t.label} ${t.sharePct}% · ${SOURCES[t.tier]}`}</Typography>
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </Stack>
    </ProfileSection>
);
